import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/reducers";
import { DomusoApiService } from "../api/DomusoApiService";
import { uniq } from "lodash";
import { StorageKeys } from "../enums/storage-keys";
import * as storage from "../utils/storage";
const { load } = storage;

export const useBannerMessageInformation = ({ reservations }: Record<string, any>) => {
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const seenBannerMsgIds = useSelector((state: RootState) => state.bannerMessage.seenIds);
  const logger = useSelector((state: RootState) => state.logger);
  const [isLoading, setIsLoading] = useState<any>(true);
  const [bannerMessages, setBannerMessages] = useState<any>();
  const [hasError, setHasError] = useState<any>();
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);

  const getAllBannerMsgs = async () => {
    let propIds = reservations.map(({ property }: Record<string, any>) => property.id);
    propIds = uniq(propIds);
    const promises = propIds.map((id: string) =>
      domusoApiService.getBannerMessage(id).catch((error) => ({ error }))
    );
    let bannerMsgs = await Promise.all(promises);
    return bannerMsgs.filter((msg: any) => msg && !Boolean(msg?.error));
  };

  const bannerMsgsWithPropNames = (
    bannerMsgs: Array<Record<string, any>>
  ): Array<Record<string, any>> => {
    let propNameMap = new Map();
    const companyIdPropertyMap = new Map();
    const propSpecificIdMap = new Map();
    bannerMsgs.forEach(({ prop_id }) => (prop_id ? propSpecificIdMap.set(prop_id, 1) : null));
    reservations.forEach(
      ({ prop_id, prop_name, property: { company_id } }: Record<string, any>) => {
        propNameMap.set(prop_id, prop_name);
        if (company_id && !propSpecificIdMap.has(prop_id)) {
          let propNameArrayForCompany = companyIdPropertyMap.get(company_id);
          if (!propNameArrayForCompany) propNameArrayForCompany = [];
          propNameArrayForCompany.push(prop_name);
          companyIdPropertyMap.set(company_id, propNameArrayForCompany);
        }
      }
    );
    bannerMsgs = bannerMsgs.map(({ prop_id, company_id, ...msg }) => {
      let propName;
      if (prop_id) {
        propName = propNameMap.get(prop_id);
      } else if (company_id) {
        const propNameArrayForCompany = companyIdPropertyMap.get(company_id);
        propName = propNameArrayForCompany.pop();
        companyIdPropertyMap.set(company_id, propNameArrayForCompany);
      }
      return { ...msg, prop_name: propName };
    });
    return bannerMsgs as Array<Record<string, any>>;
  };

  const bannerMsgsNotSeen = (bannerMsgs: Array<any>): Array<Record<string, any>> => {
    if (seenBannerMsgIds && seenBannerMsgIds.length) {
      bannerMsgs = bannerMsgs.filter((msg: any) => !Array.from(seenBannerMsgIds).includes(msg.id));
    }
    return bannerMsgs;
  };

  useEffect(() => {
    (async () => {
      try {
        if (!(reservations && Array.isArray(reservations))) return;
        let bannerMsgs = await getAllBannerMsgs();
        if (bannerMsgs && Array.isArray(bannerMsgs)) {
          bannerMsgs = bannerMsgsWithPropNames(bannerMsgs);
          bannerMsgs = bannerMsgsNotSeen(bannerMsgs);
          setBannerMessages(bannerMsgs);
        } else {
          setBannerMessages([]);
        }
      } catch (error) {
        logger.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [reservations]);

  return { bannerMessages, isBannerMessageLoading: isLoading };
};
